'use client'

import * as React from 'react'
import { usePathname } from 'next/navigation'
import { clsx } from 'clsx'
import {
  // ChatBubbleIcon,
  CommitIcon,
  CountdownTimerIcon,
  GearIcon,
  Pencil1Icon,
} from '@radix-ui/react-icons'
import { ButtonLink, ButtonLinkProps } from '~/sotano-kit/primitives/button'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useWorkingDiff } from '~/lib/diff/use-working-diff'
import LoadingIcon from '../(editor)/explore/components/blocks/empty-state/loading-icon'
import { isWindows } from 'react-device-detect'
import { useRepoKeyboardNavigation } from '~/hooks/use-repo-keyboard-navigation'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { sidebarLocalStorageKeys } from '~/lib/dom-keys'
import { BranchMergeIcon } from '~/components/icons/branch-icon'
import { useFlags } from '~/lib/flags/provider'
import { ChangelogIcon } from '~/components/icons/changelog-icon'
import { BranchSwitcher } from '../../../header/branch-switcher'
import { Portal } from '~/components/portal'
import { SetRequired } from 'type-fest'
import { useBranchMergeDiff } from '../(editor)/branch/_hooks/use-merge-diff'
import {
  useSelectedBranch,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import { DevIcon } from '~/components/icons/dev'
import {
  useIsEditorOrHigher,
  useIsMemberOrHigher,
} from '~/hooks-data/use-user-role'
import { isCommit } from '@basehub/data/replicache/index'
import { dropDatabase } from 'replicache'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useClientDebugger } from '~/hooks/use-client-debugger'
import { AnnouncementHighlight } from '~/sotano-kit/primitives/announcement-highlight'

export type NavItem = {
  id: string
  tooltip: string
  shortcutIndex?: number
  icon: React.ReactNode
  active: boolean
  linkProps: SetRequired<
    Pick<
      ButtonLinkProps,
      'href' | 'prefetch' | 'target' | 'takeActionOnMouseDown'
    >,
    'href'
  >
}

const useRepoNavItems = ({
  isExternalVisitor = false,
}: {
  isExternalVisitor?: boolean
}) => {
  const pathname = usePathname()
  const { teamSlug, repoPage, repoSlug } = useDashboardSlugs((s) => s)
  const rest = pathname.split('/').slice(4).join('/')
  const hasRendered = useHasRendered()
  const { branching } = useFlags()
  const isMemberOrHigher = useIsMemberOrHigher()
  const isEditorOrHigher = useIsEditorOrHigher()

  return React.useMemo(() => {
    let shortcutIndex = 1

    const navItems: NavItem[] = [
      {
        id: 'explore',
        tooltip: `Explore`,
        shortcutIndex: shortcutIndex++,
        icon: (
          <Pencil1Icon width={16} height={16} className="pointer-events-none" />
        ),
        active: hasRendered ? repoPage === 'explore' : false,
        linkProps: {
          href: !hasRendered ? '#' : `/${teamSlug}/${repoSlug}/explore/${rest}`,
          prefetch: Boolean(teamSlug && repoSlug),
          takeActionOnMouseDown: true,
        },
      },
      ...(isExternalVisitor
        ? []
        : [
            {
              id: 'changes',
              tooltip: `Changes`,
              shortcutIndex: shortcutIndex++,
              icon: (
                <CommitIcon
                  width={16}
                  height={16}
                  className="pointer-events-none"
                />
              ),
              active: hasRendered ? repoPage === 'changes' : false,
              linkProps: {
                href: !hasRendered
                  ? '#'
                  : `/${teamSlug}/${repoSlug}/changes/${rest}`,
                prefetch: Boolean(teamSlug && repoSlug),
                takeActionOnMouseDown: true,
              },
            },
          ]),
      {
        id: 'history',
        tooltip: `History`,
        shortcutIndex: shortcutIndex++,
        icon: (
          <CountdownTimerIcon
            width={16}
            height={16}
            className="pointer-events-none"
          />
        ),
        active: hasRendered ? repoPage === 'history' : false,
        linkProps: {
          prefetch: Boolean(teamSlug && repoSlug),
          href: !hasRendered ? '#' : `/${teamSlug}/${repoSlug}/history/${rest}`,
          takeActionOnMouseDown: true,
        },
      },
      // {
      //   id: 'chats',
      //   tooltip: `Chats`,
      //   shortcutIndex: shortcutIndex++,
      //   icon: (
      //     <ChatBubbleIcon
      //       width={16}
      //       height={16}
      //       className="pointer-events-none"
      //     />
      //   ),
      //   active: hasRendered ? repoPage === 'chats' : false,
      //   linkProps: {
      //     prefetch: Boolean(teamSlug && repoSlug),
      //     href: !hasRendered ? '#' : `/${teamSlug}/${repoSlug}/chats/${rest}`,
      //     takeActionOnMouseDown: true,
      //   },
      // },
      ...(isExternalVisitor || !branching
        ? []
        : [
            {
              id: 'branch',
              tooltip: `Branch`,
              shortcutIndex: shortcutIndex++,
              icon: (
                <BranchMergeIcon
                  width={16}
                  height={16}
                  className="pointer-events-none"
                />
              ),
              active: hasRendered ? repoPage === 'branch' : false,
              linkProps: {
                href: !hasRendered
                  ? '#'
                  : `/${teamSlug}/${repoSlug}/branch/${rest}`,
                prefetch: Boolean(teamSlug && repoSlug),
                takeActionOnMouseDown: true,
              },
            },
          ]),
      ...(!isEditorOrHigher
        ? []
        : [
            {
              id: 'agents',
              tooltip: `Agents`,
              shortcutIndex: shortcutIndex++,
              icon: (
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="pointer-events-none"
                >
                  <path
                    d="M5.93164 10.3885C6.48393 10.3885 6.93164 9.94074 6.93164 9.38845C6.93164 8.83617 6.48393 8.38845 5.93164 8.38845C5.37936 8.38845 4.93164 8.83617 4.93164 9.38845C4.93164 9.94074 5.37936 10.3885 5.93164 10.3885Z"
                    fill="currentColor"
                  />
                  <path
                    d="M10.0588 10.3885C10.6111 10.3885 11.0588 9.94074 11.0588 9.38845C11.0588 8.83617 10.6111 8.38845 10.0588 8.38845C9.5065 8.38845 9.05879 8.83617 9.05879 9.38845C9.05879 9.94074 9.5065 10.3885 10.0588 10.3885Z"
                    fill="currentColor"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M14.1111 8.83336V6.61114C14.1111 5.53725 13.2406 4.66669 12.1667 4.66669H8.55556V3.44447C8.55556 3.13765 8.30682 2.88892 8 2.88892C7.69319 2.88899 7.44444 3.13773 7.44444 3.44454V4.66669H3.83333C2.75945 4.66669 1.88889 5.53725 1.88889 6.61114V8.83336H1.05556C0.748731 8.83336 0.5 9.08209 0.5 9.38892C0.5 9.69574 0.748731 9.94447 1.05556 9.94447H1.88889V12.1667C1.88889 13.2406 2.75945 14.1111 3.83333 14.1111H12.1667C13.2406 14.1111 14.1111 13.2406 14.1111 12.1667V9.94447H14.9444C15.2513 9.94447 15.5 9.69574 15.5 9.38892C15.5 9.08209 15.2513 8.83336 14.9444 8.83336H14.1111ZM13 12.1667V9.38892V6.61114C13 6.1509 12.6269 5.7778 12.1667 5.7778H8H3.83333C3.3731 5.7778 3 6.1509 3 6.61114V9.38892V12.1667C3 12.6269 3.3731 13 3.83333 13H12.1667C12.6269 13 13 12.6269 13 12.1667Z"
                    fill="currentColor"
                  />
                </svg>
              ),
              active: hasRendered ? repoPage === 'agents' : false,
              linkProps: {
                href: !hasRendered
                  ? '#'
                  : `/${teamSlug}/${repoSlug}/agents/${rest}`,
                prefetch: Boolean(teamSlug && repoSlug),
                takeActionOnMouseDown: true,
              },
            },
          ]),
    ]

    const footerNavItems: NavItem[] = [
      ...(isMemberOrHigher
        ? [
            {
              id: 'dev',
              tooltip: `Developers`,
              shortcutIndex: shortcutIndex++,
              icon: (
                <DevIcon
                  className="pointer-events-none"
                  width={16}
                  height={16}
                />
              ),
              active: hasRendered ? repoPage === 'dev' : false,
              linkProps: {
                href: !hasRendered
                  ? '#'
                  : `/${teamSlug}/${repoSlug}/dev/${rest}`,
                prefetch: Boolean(teamSlug && repoSlug),
                takeActionOnMouseDown: true,
              },
            },
            {
              id: 'settings',
              tooltip: `Settings`,
              shortcutIndex: shortcutIndex++,
              icon: (
                <GearIcon
                  className="pointer-events-none"
                  width={16}
                  height={16}
                />
              ),
              active: hasRendered ? repoPage === 'settings' : false,
              linkProps: {
                href: !hasRendered
                  ? '#'
                  : `/${teamSlug}/${repoSlug}/settings/${rest}`,
                prefetch: Boolean(teamSlug && repoSlug),
                takeActionOnMouseDown: true,
              },
            },
          ]
        : []),
      {
        id: 'changelog',
        tooltip: `Changelog`,
        shortcutIndex: shortcutIndex++,
        icon: (
          <ChangelogIcon
            className="pointer-events-none"
            width={16}
            height={16}
          />
        ),
        active: hasRendered ? repoPage === 'changelog' : false,
        linkProps: {
          href: !hasRendered
            ? '#'
            : `/${teamSlug}/${repoSlug}/changelog/${rest}`,
          prefetch: Boolean(teamSlug && repoSlug),
          takeActionOnMouseDown: true,
        },
      },
      {
        id: 'help',
        tooltip: `Help Center (F1)`,
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8.3135 13.6509C8.14552 13.4829 8.14552 13.2105 8.3135 13.0426L14.0314 7.32466H10.3382C10.1007 7.32466 9.9081 7.13208 9.9081 6.89452C9.9081 6.65695 10.1007 6.46437 10.3382 6.46437L15.0699 6.46437C15.1839 6.46437 15.2933 6.50969 15.374 6.59036C15.4547 6.67102 15.5 6.78043 15.5 6.89452L15.5 11.6261C15.5 11.8637 15.3074 12.0563 15.0699 12.0563C14.8323 12.0563 14.6397 11.8637 14.6397 11.6261L14.6397 7.93298L8.92182 13.6509C8.75384 13.8189 8.48148 13.8189 8.3135 13.6509Z"
              fill="currentColor"
            />
            <path
              d="M0.5 7.99994C0.5 4.62991 3.23196 1.89795 6.60199 1.89795C8.87512 1.89795 10.858 3.14092 11.9079 4.98427H10.8715C9.92488 3.64655 8.36541 2.77324 6.60199 2.77324C3.71537 2.77324 1.37529 5.11332 1.37529 7.99994C1.37529 10.8865 3.71537 13.2266 6.60199 13.2266V14.1019C3.23196 14.1019 0.5 11.3699 0.5 7.99994Z"
              fill="currentColor"
            />
            <path
              d="M7.29289 10.7642C7.29289 11.1459 6.98351 11.4553 6.60187 11.4553C6.22024 11.4553 5.91086 11.1459 5.91086 10.7642C5.91086 10.3826 6.22024 10.0732 6.60187 10.0732C6.98351 10.0732 7.29289 10.3826 7.29289 10.7642Z"
              fill="currentColor"
            />
            <path
              d="M5.26613 6.84847C5.26613 6.22388 5.8052 5.62767 6.6021 5.62767C7.399 5.62767 7.93807 6.22388 7.93807 6.84847C7.93807 7.30102 7.68929 7.53222 7.26035 7.7928C7.21252 7.82186 7.15736 7.8539 7.09835 7.88817C6.92957 7.98619 6.72909 8.10261 6.57857 8.2199C6.35528 8.39388 6.09535 8.67458 6.09535 9.10579C6.09535 9.38566 6.32223 9.61254 6.6021 9.61254C6.88069 9.61254 7.10727 9.38693 7.10933 9.10883C7.11733 9.09621 7.141 9.06649 7.20148 9.01936C7.29499 8.9465 7.39911 8.88616 7.53786 8.80576C7.6102 8.76384 7.692 8.71643 7.78656 8.65899C8.27898 8.35984 8.95156 7.86548 8.95156 6.84847C8.95156 5.63033 7.92432 4.61418 6.6021 4.61418C5.27988 4.61418 4.25264 5.63033 4.25264 6.84847C4.25264 7.12833 4.47952 7.35521 4.75939 7.35521C5.03925 7.35521 5.26613 7.12833 5.26613 6.84847Z"
              fill="currentColor"
            />
          </svg>
        ),
        active: false,
        linkProps: {
          href: '/help?chat=true',
          prefetch: false,
          target: '_blank',
        },
      },
    ]

    return { navItems, footerNavItems }
  }, [
    branching,
    hasRendered,
    isExternalVisitor,
    isMemberOrHigher,
    repoPage,
    repoSlug,
    rest,
    teamSlug,
    isEditorOrHigher,
  ])
}

export const RepoNav: React.FC<{ isExternalVisitor?: boolean }> = ({
  isExternalVisitor,
}) => {
  const navItemsRef = React.useRef<
    Array<HTMLAnchorElement | HTMLButtonElement>
  >([])
  const [hoveredItemIndex, setHoveredItemIndex] = React.useState<number | null>(
    null
  )
  const { navItems, footerNavItems } = useRepoNavItems({ isExternalVisitor })
  const itemTooltips = React.useMemo(() => {
    return [...navItems, ...footerNavItems].map((item) => {
      return `${item.tooltip}${
        typeof item.shortcutIndex === 'number'
          ? ` (${isWindows ? 'Ctrl+' : '⌃'}${item.shortcutIndex})`
          : ''
      }`
    })
  }, [footerNavItems, navItems])
  const hoveredItemTooltip = React.useMemo(
    () => itemTooltips[hoveredItemIndex ?? -1] ?? '',
    [hoveredItemIndex, itemTooltips]
  )

  const { firstItemTop, hoveredItemEl, hoveredItemHeight, hoveredItemTop } =
    React.useMemo(() => {
      if (typeof document === 'undefined') return {}
      const itemsJoined = [...navItems, ...footerNavItems]
      const firstItem = itemsJoined[0]
      const firstItemEl = firstItem
        ? document.getElementById(`repo-nav-item-${firstItem.id}`)
        : null
      const firstItemTop = firstItemEl?.offsetTop ?? 0

      const hoveredItem = itemsJoined[hoveredItemIndex ?? -1]
      const hoveredItemEl = hoveredItem
        ? document.getElementById(`repo-nav-item-${hoveredItem.id}`)
        : null
      const hoveredItemHeight = hoveredItemEl?.offsetHeight ?? 0
      const hoveredItemTop = hoveredItemEl?.offsetTop ?? 0

      return {
        firstItemTop,
        hoveredItemEl,
        hoveredItemTop,
        hoveredItemHeight,
      }
    }, [footerNavItems, hoveredItemIndex, navItems])

  const totalLength = React.useMemo(
    () => navItems.length + footerNavItems.length,
    [navItems, footerNavItems]
  )

  useRepoKeyboardNavigation({ disabled: false, navItems, footerNavItems })

  const { branching } = useFlags()

  const renderNavItem = React.useCallback(
    ({ id, active, linkProps, icon, tooltip }: NavItem, index: number) => {
      switch (id) {
        case 'changes':
          return (
            <ChangesLink
              aria-label={tooltip}
              key={id}
              id={`repo-nav-item-${id}`}
              ref={(ref) => {
                ref &&
                  navItemsRef.current.length < totalLength &&
                  navItemsRef.current.push(ref)
              }}
              onMouseLeave={() => setHoveredItemIndex(null)}
              onMouseEnter={() => setHoveredItemIndex(index)}
              onFocus={() => setHoveredItemIndex(index)}
              onBlur={() => setHoveredItemIndex(null)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  const pretendToHoverIndex = (hoveredItemIndex ?? index) + 1
                  const newTargetNode = navItemsRef.current[pretendToHoverIndex]
                  if (!newTargetNode) return
                  setHoveredItemIndex(pretendToHoverIndex)
                  newTargetNode.focus()
                } else if (e.key === 'ArrowUp') {
                  const pretendToHoverIndex = (hoveredItemIndex ?? index) - 1
                  const newTargetNode = navItemsRef.current[pretendToHoverIndex]
                  if (!newTargetNode) return
                  setHoveredItemIndex(pretendToHoverIndex)
                  newTargetNode.focus()
                }
              }}
              active={active}
              {...linkProps}
            />
          )
        case 'branch':
          return (
            <BranchLink
              aria-label={tooltip}
              key={id}
              id={`repo-nav-item-${id}`}
              ref={(ref) => {
                ref &&
                  navItemsRef.current.length < totalLength &&
                  navItemsRef.current.push(ref)
              }}
              onMouseLeave={() => setHoveredItemIndex(null)}
              onMouseEnter={() => setHoveredItemIndex(index)}
              onFocus={() => setHoveredItemIndex(index)}
              onBlur={() => setHoveredItemIndex(null)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  const pretendToHoverIndex = (hoveredItemIndex ?? index) + 1
                  const newTargetNode = navItemsRef.current[pretendToHoverIndex]
                  if (!newTargetNode) return
                  setHoveredItemIndex(pretendToHoverIndex)
                  newTargetNode.focus()
                } else if (e.key === 'ArrowUp') {
                  const pretendToHoverIndex = (hoveredItemIndex ?? index) - 1
                  const newTargetNode = navItemsRef.current[pretendToHoverIndex]
                  if (!newTargetNode) return
                  setHoveredItemIndex(pretendToHoverIndex)
                  newTargetNode.focus()
                }
              }}
              active={active}
              {...linkProps}
            />
          )
        default: {
          return (
            <AnnouncementWrapper key={id} tooltip={tooltip}>
              <ButtonLink
                aria-label={tooltip}
                key={id}
                id={`repo-nav-item-${id}`}
                onMouseLeave={() => setHoveredItemIndex(null)}
                onMouseEnter={() => setHoveredItemIndex(index)}
                onFocus={() => setHoveredItemIndex(index)}
                onBlur={() => setHoveredItemIndex(null)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    const pretendToHoverIndex = (hoveredItemIndex ?? index) + 1
                    const newTargetNode =
                      navItemsRef.current[pretendToHoverIndex]
                    if (!newTargetNode) return
                    setHoveredItemIndex(pretendToHoverIndex)
                    newTargetNode.focus()
                  } else if (e.key === 'ArrowUp') {
                    const pretendToHoverIndex = (hoveredItemIndex ?? index) - 1
                    const newTargetNode =
                      navItemsRef.current[pretendToHoverIndex]
                    if (!newTargetNode) return
                    setHoveredItemIndex(pretendToHoverIndex)
                    newTargetNode.focus()
                  }
                }}
                {...linkProps}
                ref={(ref) => {
                  ref &&
                    navItemsRef.current.length < totalLength &&
                    navItemsRef.current.push(ref)
                }}
                unstyled
                className={clsx(
                  active
                    ? 'bg-dark-border-solid text-dark-bright'
                    : 'text-dark-faint hover:text-dark-bright',
                  'block cursor-default p-3 focus:text-dark-bright focus-visible:text-dark-bright focus:outline-none focus-visible:outline-none'
                )}
              >
                {icon}
              </ButtonLink>
            </AnnouncementWrapper>
          )
        }
      }
    },
    [hoveredItemIndex, totalLength]
  )

  return (
    <div
      id={sidebarLocalStorageKeys.repoNavWidth}
      className="flex flex-col bg-dark-shade px-3 pt-1 pb-2 w-repo-left-nav"
    >
      {branching && (
        <Portal id="branch-switcher-portal">
          <BranchSwitcher />
        </Portal>
      )}

      {hoveredItemEl && (
        <ManualTooltip
          height={hoveredItemHeight}
          content={hoveredItemTooltip ?? ''}
          style={{
            // translate to be at the right of the nav item that is hovered, using the ref ot the nav item list at the current index
            display: hoveredItemEl !== null ? 'inline' : 'none',
            transform: `translate(calc(12px + ${hoveredItemEl.offsetWidth}px), calc(${hoveredItemTop}px - ${firstItemTop}px))`,
          }}
        />
      )}
      {navItems.map(renderNavItem)}

      <footer className="mt-auto pt-6">
        {footerNavItems.map((item, index) =>
          renderNavItem(item, index + navItems.length)
        )}
      </footer>
    </div>
  )
}

const AnnouncementWrapper = ({
  children,
  tooltip,
}: {
  children: React.ReactNode
  tooltip: string
}) => {
  if (tooltip !== 'Agents') return children
  return (
    <AnnouncementHighlight
      announcementId="agents"
      glow={false}
      side="right"
      align="start"
    >
      {children}
    </AnnouncementHighlight>
  )
}

export const RepoNavPlaceholder = () => {
  return (
    <div
      id={sidebarLocalStorageKeys.repoNavWidth}
      className="flex flex-col bg-dark-shade px-4 pt-1 pb-2 w-repo-left-nav"
    />
  )
}

const ChangesLink = React.forwardRef<
  HTMLAnchorElement,
  Omit<
    Extract<ButtonLinkProps, { unstyled: true }>,
    'unstyled' | 'className'
  > & { active: boolean }
>(({ active, ...rest }, ref) => {
  const { data: selectedBranchOrCommit } = useSelectedBranchOrCommitQuery()
  const { diff, error, isLoading, enabled, treeNew, treeOld } = useWorkingDiff()
  const isLoadingDiff = !diff
  const replicache = useRepoReplicache().data?.replicache
  const idbName = replicache?.idbName
  const [pendingMutations, setPendingMutations] = React.useState<unknown>(null)
  const { isDebugging } = useClientDebugger()

  React.useEffect(() => {
    if (!replicache) return

    let mounted = true

    async function poll() {
      while (mounted) {
        const mutations = await replicache?.experimentalPendingMutations()
        if (mounted) {
          setPendingMutations(mutations)
        }
        await new Promise((resolve) => setTimeout(resolve, 5000))
      }
    }

    poll()

    return () => {
      mounted = false
    }
  }, [replicache])

  React.useEffect(() => {
    const refreshReplicacheCount = JSON.parse(
      window.sessionStorage.getItem('refresh-replicache-count') || '0'
    ) as number
    const shouldRefreshReplicache =
      enabled &&
      idbName &&
      isLoadingDiff &&
      (error || !isLoading) &&
      refreshReplicacheCount < 1
    if (shouldRefreshReplicache) {
      console.log(
        `Will refresh replicache. Reason: ${
          error ? `error: ${error}.` : 'not loading, no data.'
        }`
      )
      const timeout = setTimeout(() => {
        // force refresh replicache
        console.log('refreshing replicache...')
        window.sessionStorage.setItem(
          'refresh-replicache-count',
          JSON.stringify(refreshReplicacheCount + 1)
        )
        dropDatabase(idbName)
        window.location.reload()
      }, 3_000)
      return () => clearTimeout(timeout)
    }
  }, [enabled, error, idbName, isLoading, isLoadingDiff])

  if (isDebugging) {
    console.log('—————— diff loading ——————')
    console.log({
      isLoadingDiff,
      diff,
      error,
      enabled,
      pendingMutations,
      treeNew,
      treeOld,
    })
    console.log('—————— diff loading end ——————')
  }

  const isCheckingOutCommit =
    selectedBranchOrCommit && isCommit(selectedBranchOrCommit)

  return (
    <ButtonLink
      {...rest}
      ref={ref}
      unstyled
      className={clsx(
        active
          ? 'bg-dark-border-solid text-dark-bright'
          : 'text-dark-faint hover:text-dark-bright',
        'relative block cursor-default p-3 focus:text-dark-bright focus-visible:text-dark-bright focus:outline-none focus-visible:outline-none'
      )}
    >
      {isCheckingOutCommit ? (
        <></>
      ) : (
        <>
          {isLoadingDiff ? (
            <span
              aria-label="Content diff loading"
              className="absolute pointer-events-none select-none top-[65%] -translate-y-1/2 left-[65%] -translate-x-1/2 inline-flex h-3 w-3"
            >
              <LoadingIcon status="loading" className="[&>span]:size-3" />
            </span>
          ) : (
            (diff?.hasChanged || diff.changesCount > 0) && (
              <span
                aria-label={`${diff.changesCount} changes to commit`}
                className="shadow-2xl absolute min-w-max pointer-events-none select-none top-3/4 text-[11px] -translate-y-1/2 left-[70%] -translate-x-1/2 inline-flex h-[14px] w-[14px] items-center text-dark-bg p-[3px] justify-center rounded-full bg-dark-control-base text-center font-medium leading-none"
              >
                {diff.changesCount || '1'}
              </span>
            )
          )}
        </>
      )}

      <CommitIcon width={16} height={16} />
    </ButtonLink>
  )
})

const BranchLink = React.forwardRef<
  HTMLAnchorElement,
  Omit<
    Extract<ButtonLinkProps, { unstyled: true }>,
    'unstyled' | 'className'
  > & {
    active: boolean
  }
>(({ active, ...rest }, ref) => {
  const branch = useSelectedBranch().branch ?? null
  const { diffResult } = useBranchMergeDiff({
    branch,
  })

  return (
    <ButtonLink
      {...rest}
      ref={ref}
      unstyled
      className={clsx(
        active
          ? 'bg-dark-border-solid text-dark-bright'
          : 'text-dark-faint hover:text-dark-bright',
        'relative block cursor-default p-3 focus:text-dark-bright focus-visible:text-dark-bright focus:outline-none focus-visible:outline-none'
      )}
    >
      {diffResult.isLoading ? (
        <span
          aria-label="Content diff loading"
          className="absolute pointer-events-none select-none top-[65%] -translate-y-1/2 left-[65%] -translate-x-1/2 inline-flex h-3 w-3"
        >
          <LoadingIcon status="loading" className="[&>span]:size-3" />
        </span>
      ) : (
        Boolean(diffResult.diff?.changesCount) && (
          <span
            aria-label={`${diffResult.diff?.changesCount} changes to commit`}
            className="shadow-2xl absolute min-w-max pointer-events-none select-none top-3/4 text-[11px] -translate-y-1/2 left-[70%] -translate-x-1/2 inline-flex h-[14px] w-[14px] items-center text-dark-bg p-[3px] justify-center rounded-full bg-dark-control-base text-center font-medium leading-none"
          >
            {diffResult.diff?.changesCount}
          </span>
        )
      )}

      <BranchMergeIcon width={16} height={16} className="pointer-events-none" />
    </ButtonLink>
  )
})

const ManualTooltip: React.FC<{
  content: string
  height: number
  style?: React.CSSProperties
}> = ({ content, height, style }) => {
  return (
    <span
      style={{ height }}
      className="flex items-center absolute pointer-events-none z-tooltip"
    >
      <span
        style={style}
        className={clsx(
          'dark:shadow-none cursor-default select-none max-w-[264px] rounded-md border border-invert-border bg-invert-base py-1 px-2 text-xs leading-snug text-invert-dim font-normal',
          'after:content-[""] after:absolute after:top-1/2 after:-translate-y-1/2 after:-left-1 after:w-1.5 after:h-1.5 after:bg-invert-base after:border-l after:border-b after:border-invert-border after:rotate-45'
        )}
      >
        {content}
      </span>
    </span>
  )
}
